import { type RefObject, useEffect, useState } from "react";

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

type UseHeroScrollProgressOptions = {
  sectionRef: RefObject<HTMLElement | null>;
  slideCount: number;
  reducedMotion: boolean;
};

/**
 * Progresso 0–1 ao longo da secção externa (altura N×viewport), com trilho de N slides.
 * `translatePercent` é % do próprio trilho: `-progress * (n-1)/n * 100`.
 */
export function useHeroScrollProgress({
  sectionRef,
  slideCount,
  reducedMotion,
}: UseHeroScrollProgressOptions) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(0);
      return;
    }

    let raf = 0;

    const update = () => {
      raf = 0;
      const el = sectionRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const h = el.offsetHeight;
      const vh = window.innerHeight;
      const max = Math.max(1, h - vh);
      const raw = window.scrollY - top;
      const p = Math.min(1, Math.max(0, raw / max));
      setProgress(p);
    };

    const onScroll = () => {
      if (raf === 0) raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [sectionRef, slideCount, reducedMotion]);

  const n = Math.max(1, slideCount);
  const translatePercent = reducedMotion ? 0 : (-progress * (n - 1) * 100) / n;

  const activeIndex = reducedMotion
    ? 0
    : Math.min(n - 1, Math.round(progress * (n - 1)));

  return { progress, translatePercent, activeIndex, slideCount: n };
}
